import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ThemeProviderContext } from "@/contexts/theme-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { CompanyDTO, CompanyEstablishment } from "@shaarp/common";
import { BuildingIcon, Loader2Icon } from "lucide-react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type CardCompanyEntitiesGraphProps = {
  readonly company: CompanyDTO;
};

type EstablishmentStatus = "headquarters" | "active" | "ceased";

const STATUS_COLORS: Record<EstablishmentStatus, string> = {
  headquarters: "#1e40af",
  active: "#1e293b",
  ceased: "#9ca3af",
};

function getStatus(est: CompanyEstablishment): EstablishmentStatus {
  if (est.isHeadquarters) return "headquarters";
  if (est.isCeased) return "ceased";
  return "active";
}

// ─── Map View ───────────────────────────────────────────────────────────

const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#263c3f" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6b9a76" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f2835" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#f3d19c" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f3948" }] },
  { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#515c6d" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#17263c" }] },
];

/** Builds the address string for geocoding. */
function buildGeocodingAddress(est: CompanyEstablishment): string | null {
  const parts = [est.address, est.zipCode, est.city].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
}

/** Creates an SVG pin marker URL with the given color. */
function createMarkerIcon(color: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="${color}"/>
    <circle cx="14" cy="14" r="6" fill="white"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

type GeocodedEstablishment = CompanyEstablishment & {
  lat: number;
  lng: number;
};

function EstablishmentsMap({
  company,
}: CardCompanyEntitiesGraphProps) {
  const { isLightTheme } = useContext(ThemeProviderContext);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [geocoded, setGeocoded] = useState<GeocodedEstablishment[]>([]);
  const [selectedSiret, setSelectedSiret] = useState<string | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });

  const establishments = useMemo(
    () => company.establishments ?? [],
    [company.establishments],
  );

  // Geocode establishments
  useEffect(() => {
    if (!isLoaded || establishments.length === 0) return;

    if (!geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }
    const geocoder = geocoderRef.current;

    let cancelled = false;

    async function geocodeAll(): Promise<void> {
      const results: GeocodedEstablishment[] = [];

      for (const est of establishments) {
        if (cancelled) return;

        const address = buildGeocodingAddress(est);
        if (!address) continue;

        try {
          const response = await geocoder.geocode({ address });
          if (response.results[0]) {
            const location = response.results[0].geometry.location;
            results.push({ ...est, lat: location.lat(), lng: location.lng() });
          }
        } catch {
          // Skip establishments that cannot be geocoded
        }
      }

      if (!cancelled) {
        setGeocoded(results);
      }
    }

    geocodeAll();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, establishments]);

  // Fit bounds when geocoded results change
  useEffect(() => {
    if (!map || geocoded.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    geocoded.forEach((est) => bounds.extend({ lat: est.lat, lng: est.lng }));
    map.fitBounds(bounds);

    if (geocoded.length === 1) {
      map.setZoom(14);
    }
  }, [map, geocoded]);

  // Dark mode
  useEffect(() => {
    if (!map) return;
    map.setOptions({ styles: isLightTheme ? [] : DARK_MAP_STYLES });
  }, [map, isLightTheme]);

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const selectedEst = geocoded.find((e) => e.siret === selectedSiret);

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center w-full min-h-[400px] rounded-lg border border-border">
        <Loader2Icon className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full min-h-[400px] rounded-lg overflow-hidden border border-border">
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={{ lat: 46.603354, lng: 1.888334 }}
        zoom={6}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      >
        {geocoded.map((est) => {
          const status = getStatus(est);
          const color = STATUS_COLORS[status];

          return (
            <Marker
              key={est.siret}
              position={{ lat: est.lat, lng: est.lng }}
              icon={{
                url: createMarkerIcon(color),
                scaledSize: new google.maps.Size(28, 40),
                anchor: new google.maps.Point(14, 40),
              }}
              zIndex={est.isHeadquarters ? 1000 : est.isCeased ? 1 : 10}
              onClick={() => setSelectedSiret(est.siret)}
            />
          );
        })}
        {selectedEst && (
          <InfoWindow
            position={{ lat: selectedEst.lat, lng: selectedEst.lng }}
            onCloseClick={() => setSelectedSiret(null)}
          >
            <div className="space-y-1.5 text-sm text-black min-w-[180px]">
              <div className="font-semibold">
                {selectedEst.name ?? selectedEst.city ?? selectedEst.siret}
              </div>
              <div className="font-mono text-xs text-gray-500">
                {selectedEst.siret}
              </div>
              {selectedEst.address && (
                <div className="text-xs">{selectedEst.address}</div>
              )}
              {(selectedEst.zipCode ?? selectedEst.city) && (
                <div className="text-xs">
                  {[selectedEst.zipCode, selectedEst.city].filter(Boolean).join(" ")}
                </div>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}

// ─── Shared Components ──────────────────────────────────────────────────

function LegendItem({
  color,
  label,
}: {
  readonly color: string;
  readonly label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="w-3 h-3 rounded-full shrink-0"
        style={{ backgroundColor: color }}
      />
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function EstablishmentsTable({ company }: CardCompanyEntitiesGraphProps) {
  const { t } = useTranslation();
  const establishments = useMemo(() => {
    const list = company.establishments ?? [];
    return [...list].sort((a, b) => {
      if (a.isCeased === b.isCeased) return 0;
      return a.isCeased ? 1 : -1;
    });
  }, [company.establishments]);

  return (
    <div className="overflow-auto max-h-[500px]">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card">
          <tr className="border-b">
            <th className="py-2 pr-4 font-medium text-left text-muted-foreground">
              SIRET
            </th>
            <th className="py-2 px-4 font-medium text-left text-muted-foreground">
              {t("company.financials.establishments.address")}
            </th>
            <th className="py-2 px-4 font-medium text-left text-muted-foreground">
              {t("company.financials.establishments.city")}
            </th>
            <th className="py-2 pl-4 font-medium text-left text-muted-foreground">
              {t("company.financials.establishments.status")}
            </th>
          </tr>
        </thead>
        <tbody>
          {establishments.map((est) => (
              <tr
                key={est.siret}
                className={cn(
                  "border-b last:border-b-0",
                  est.isCeased && "bg-muted/50",
                )}
              >
                <td className="py-2.5 pr-4 font-mono text-xs">
                  {est.siret}
                </td>
                <td className="py-2.5 px-4 max-w-[200px] truncate">
                  {est.address ?? "—"}
                </td>
                <td className="py-2.5 px-4">
                  {[est.zipCode, est.city].filter(Boolean).join(" ") || "—"}
                </td>
                <td className="py-2.5 pl-4">
                  <div className="flex items-center gap-1.5">
                    {est.isHeadquarters && (
                      <Badge className="text-[10px] px-1.5 py-0 text-white" style={{ backgroundColor: STATUS_COLORS.headquarters }}>
                        {t("company.financials.establishments.headquarters")}
                      </Badge>
                    )}
                    {est.isCeased && (
                      <Badge className="text-[10px] px-1.5 py-0 text-white" style={{ backgroundColor: STATUS_COLORS.ceased }}>
                        {t("company.financials.establishments.ceased")}
                      </Badge>
                    )}
                    {!est.isHeadquarters && !est.isCeased && (
                      <Badge className="text-[10px] px-1.5 py-0 text-white" style={{ backgroundColor: STATUS_COLORS.active }}>
                        {t("company.financials.establishments.active")}
                      </Badge>
                    )}
                  </div>
                </td>
              </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Card ──────────────────────────────────────────────────────────

export default function CardCompanyEntitiesGraph({
  company,
}: CardCompanyEntitiesGraphProps) {
  const { t } = useTranslation();
  const establishments = company.establishments ?? [];

  if (establishments.length === 0) return null;

  const hasHQ = establishments.some((e) => e.isHeadquarters);
  const hasActive = establishments.some((e) => !e.isHeadquarters && !e.isCeased);
  const hasCeased = establishments.some((e) => e.isCeased);

  const legendItems: { color: string; label: string }[] = [];
  if (hasHQ) {
    legendItems.push({
      color: STATUS_COLORS.headquarters,
      label: t("company.entities.headquarters"),
    });
  }
  if (hasActive) {
    legendItems.push({
      color: STATUS_COLORS.active,
      label: t("company.entities.active"),
    });
  }
  if (hasCeased) {
    legendItems.push({
      color: STATUS_COLORS.ceased,
      label: t("company.entities.ceased"),
    });
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3 text-lg">
            <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-muted">
              <BuildingIcon className="w-5 h-5" />
            </div>
            {t("company.entities.title")}
          </CardTitle>
          <div className="flex items-center gap-4">
            {legendItems.map((item) => (
              <LegendItem key={item.label} color={item.color} label={item.label} />
            ))}
            <span className="text-sm text-muted-foreground">
              {establishments.length}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <EstablishmentsMap company={company} />
          <EstablishmentsTable company={company} />
        </div>
      </CardContent>
    </Card>
  );
}
